import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import "../App.css";
import DigitalCard from "../components/DigitalCard";
import { DEFAULT_CARD_COLOR } from "../constants/cardDefaults";

const API_BASE = process.env.REACT_APP_API_URL || "";
const FALLBACK_ICE_SERVERS = [{ urls: ["stun:stun.l.google.com:19302", "stun:stun1.l.google.com:19302"] }];
const TRUST_VIEWER_SIGNAL_POLL_MS = 400;
const TRUST_VIEWER_TELEMETRY_FAST_POLL_MS = 180;
const TRUST_VIEWER_TELEMETRY_IDLE_POLL_MS = 1100;
const TRUST_VIEWER_TELEMETRY_ACTIVE_WINDOW_MS = 3000;
const TRUST_VIEWER_POINTER_MOVE_THROTTLE_MS = 80;
const TRUST_VIEWER_RECONNECT_REQUEST_THROTTLE_MS = 2500;
const TRUST_VIEWER_STARTUP_RECONNECT_MS = 6000;

const makeViewerKey = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `v-${Math.random().toString(16).slice(2)}-${Date.now().toString(16)}`;
};

const makeLegacyOfferId = (signal) => `legacy-offer-${signal?.id || Date.now()}`;

const resolveViewerIceServers = async ({ token, viewerKey }) => {
  if (!token || !viewerKey) return FALLBACK_ICE_SERVERS;
  try {
    const res = await fetch(
      `${API_BASE}/api/trust-session/${encodeURIComponent(token)}/ice-servers?role=viewer&viewerKey=${encodeURIComponent(viewerKey)}`,
      { cache: "no-store" }
    );
    const payload = await res.json().catch(() => ({}));
    if (!res.ok) return FALLBACK_ICE_SERVERS;
    const iceServers = Array.isArray(payload?.iceServers) ? payload.iceServers : [];
    return iceServers.length > 0 ? iceServers : FALLBACK_ICE_SERVERS;
  } catch (_err) {
    return FALLBACK_ICE_SERVERS;
  }
};

export default function TrustSessionPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sessionBundle, setSessionBundle] = useState(null);
  const [showDigitalCard, setShowDigitalCard] = useState(false);
  const [viewerKey, setViewerKey] = useState("");
  const [liveStatus, setLiveStatus] = useState("Waiting for the licensed professional to start sharing.");
  const [connectionState, setConnectionState] = useState("idle");
  const [remoteVideoReady, setRemoteVideoReady] = useState(false);
  const remoteVideoRef = useRef(null);
  const videoWrapRef = useRef(null);
  const peerRef = useRef(null);
  const pollTimerRef = useRef(null);
  const cursorRef = useRef(0);
  const initialSignalCursorRef = useRef(0);
  const pendingRemoteCandidatesRef = useRef([]);
  const activeConnectionEpochRef = useRef("");
  const activeOfferIdRef = useRef("");
  const [resumePromptVisible, setResumePromptVisible] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isInlineFullscreen, setIsInlineFullscreen] = useState(false);
  const fullscreenSupported =
    typeof Element !== "undefined" &&
    !!(
      Element.prototype.requestFullscreen ||
      Element.prototype.webkitRequestFullscreen ||
      Element.prototype.msRequestFullscreen
    );
  const canToggleFullscreen = typeof document !== "undefined";
  const isViewerFullscreen = isFullscreen || isInlineFullscreen;

  const stopRealtime = () => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    if (peerRef.current) {
      try {
        peerRef.current.ontrack = null;
        peerRef.current.onicecandidate = null;
        peerRef.current.onconnectionstatechange = null;
        peerRef.current.close();
      } catch (_err) {
        // ignore
      }
      peerRef.current = null;
    }
    pendingRemoteCandidatesRef.current = [];
    activeConnectionEpochRef.current = "";
    activeOfferIdRef.current = "";
  };

  useEffect(() => () => stopRealtime(), []);

  useEffect(() => {
    const syncFullscreenState = () => {
      if (typeof document === "undefined") return;
      const fullscreenElement =
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.msFullscreenElement ||
        null;
      setIsFullscreen(fullscreenElement === videoWrapRef.current);
    };

    document.addEventListener("fullscreenchange", syncFullscreenState);
    document.addEventListener("webkitfullscreenchange", syncFullscreenState);
    document.addEventListener("MSFullscreenChange", syncFullscreenState);
    return () => {
      document.removeEventListener("fullscreenchange", syncFullscreenState);
      document.removeEventListener("webkitfullscreenchange", syncFullscreenState);
      document.removeEventListener("MSFullscreenChange", syncFullscreenState);
    };
  }, []);

  useEffect(() => {
    if (!isInlineFullscreen) return undefined;
    const priorOverflow = document.body.style.overflow;
    const priorTouchAction = document.body.style.touchAction;
    document.body.style.overflow = "hidden";
    document.body.style.touchAction = "none";
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsInlineFullscreen(false);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = priorOverflow;
      document.body.style.touchAction = priorTouchAction;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [isInlineFullscreen]);

  useEffect(() => {
    if (!showDigitalCard) return undefined;
    const priorOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (event) => {
      if (event.key === "Escape") setShowDigitalCard(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = priorOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [showDigitalCard]);

  useEffect(() => {
    const initialize = async () => {
      if (!token) {
        setError("Missing trust session token.");
        setLoading(false);
        return;
      }
      setLoading(true);
      setError("");

      const storageKey = `clearbadge.trust.viewer.${token}`;
      const existingViewerKey = (typeof window !== "undefined" && localStorage.getItem(storageKey)) || "";
      const nextViewerKey = existingViewerKey || makeViewerKey();
      if (typeof window !== "undefined" && !existingViewerKey) {
        localStorage.setItem(storageKey, nextViewerKey);
      }

      try {
        const claimRes = await fetch(`${API_BASE}/api/trust-session/${encodeURIComponent(token)}/claim`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ viewerKey: nextViewerKey, hadStoredViewerKey: !!existingViewerKey }),
        });
        const claimPayload = await claimRes.json().catch(() => ({}));
        if (!claimRes.ok) {
          setError(claimPayload?.error || "Unable to claim trust session.");
          setLoading(false);
          return;
        }

        const resolvedViewerKey = String(claimPayload?.viewerKey || nextViewerKey).trim() || nextViewerKey;
        const signalCursor = Number(claimPayload?.signalCursor);
        initialSignalCursorRef.current = Number.isFinite(signalCursor) && signalCursor > 0 ? signalCursor : 0;
        if (typeof window !== "undefined") {
          localStorage.setItem(storageKey, resolvedViewerKey);
        }
        setViewerKey(resolvedViewerKey);

        const metaRes = await fetch(`${API_BASE}/api/trust-session/${encodeURIComponent(token)}?viewerKey=${encodeURIComponent(resolvedViewerKey)}`, {
          cache: "no-store",
        });
        const metaPayload = await metaRes.json().catch(() => ({}));
        if (!metaRes.ok) {
          setError(metaPayload?.error || "Unable to load trust session.");
          setLoading(false);
          return;
        }

        setSessionBundle(metaPayload);
        setLoading(false);
      } catch (_err) {
        setError("Unable to connect to trust session.");
        setLoading(false);
      }
    };

    initialize();
  }, [token]);

  useEffect(() => {
    if (!sessionBundle?.session || !viewerKey || !token) return undefined;

    let cancelled = false;
    pendingRemoteCandidatesRef.current = [];
    cursorRef.current = initialSignalCursorRef.current || 0;

    const sendSignal = async (signalType, payload) => {
      const res = await fetch(`${API_BASE}/api/trust-session/${encodeURIComponent(token)}/signal`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          senderRole: "viewer",
          signalType,
          payload: {
            ...payload,
            connectionEpoch: payload?.connectionEpoch || activeConnectionEpochRef.current || "",
            offerId: payload?.offerId || activeOfferIdRef.current || "",
          },
          viewerKey,
        }),
      });
      if (!res.ok) {
        const errorPayload = await res.json().catch(() => ({}));
        throw new Error(errorPayload?.error || `Unable to send ${signalType} signal.`);
      }
    };

    let telemetryTimer = null;
    let focused = typeof document !== "undefined" ? document.hasFocus() : true;
    let lastTelemetrySignature = "";
    let destroyed = false;
    let recentTelemetryActivityUntil = Date.now() + TRUST_VIEWER_TELEMETRY_ACTIVE_WINDOW_MS;
    let telemetryInFlight = false;
    let queuedTelemetryForce = false;
    let pointerSignalInFlight = false;
    let queuedPointerPayload = null;
    let lastPointerMoveAt = 0;
    let pointerSequence = 0;
    let lastReconnectRequestAt = 0;
    let realtimeStarting = false;
    let startupReconnectTimer = null;
    const isMobileViewer =
      typeof window !== "undefined" &&
      ((typeof window.matchMedia === "function" && window.matchMedia("(pointer: coarse)").matches) ||
        /iphone|ipad|ipod|android/i.test(window.navigator?.userAgent || ""));

    const clampRatio = (value) => {
      if (!Number.isFinite(value)) return 0;
      return Math.max(0, Math.min(1, value));
    };

    const getVideoContentRect = () => {
      const videoEl = remoteVideoRef.current;
      if (!videoEl) return null;
      const rect = videoEl.getBoundingClientRect();
      const elementWidth = Number(rect.width) || 0;
      const elementHeight = Number(rect.height) || 0;
      if (elementWidth <= 0 || elementHeight <= 0) return null;

      let left = rect.left;
      let top = rect.top;
      let width = elementWidth;
      let height = elementHeight;
      const sourceWidth = Number(videoEl.videoWidth) || 0;
      const sourceHeight = Number(videoEl.videoHeight) || 0;
      if (sourceWidth > 0 && sourceHeight > 0) {
        const elementAspect = elementWidth / elementHeight;
        const sourceAspect = sourceWidth / sourceHeight;
        if (sourceAspect > elementAspect) {
          height = elementWidth / sourceAspect;
          top = rect.top + Math.max(0, (elementHeight - height) / 2);
        } else if (sourceAspect < elementAspect) {
          width = elementHeight * sourceAspect;
          left = rect.left + Math.max(0, (elementWidth - width) / 2);
        }
      }

      return { left, top, width, height };
    };

    const getPointerPayload = (event, kind) => {
      const contentRect = getVideoContentRect();
      if (!contentRect || contentRect.width <= 0 || contentRect.height <= 0) return null;
      const x = clampRatio((event.clientX - contentRect.left) / contentRect.width);
      const y = clampRatio((event.clientY - contentRect.top) / contentRect.height);
      const withinFrame =
        event.clientX >= contentRect.left &&
        event.clientX <= contentRect.left + contentRect.width &&
        event.clientY >= contentRect.top &&
        event.clientY <= contentRect.top + contentRect.height;
      if (!withinFrame) return null;
      pointerSequence += 1;
      return {
        active: true,
        x,
        y,
        kind,
        pointerType: event.pointerType || "mouse",
        sequence: pointerSequence,
        updatedAt: Date.now(),
      };
    };

    const sendViewerPointer = async (payload) => {
      if (destroyed || !payload) return;
      if (pointerSignalInFlight) {
        queuedPointerPayload = payload;
        return;
      }
      pointerSignalInFlight = true;
      try {
        await sendSignal("viewer_pointer", payload);
      } catch (_err) {
        // Pointer telemetry is best-effort.
      } finally {
        pointerSignalInFlight = false;
        if (queuedPointerPayload) {
          const nextPayload = queuedPointerPayload;
          queuedPointerPayload = null;
          void sendViewerPointer(nextPayload);
        }
      }
    };

    const sendPointerInactive = () => {
      pointerSequence += 1;
      void sendViewerPointer({
        active: false,
        sequence: pointerSequence,
        updatedAt: Date.now(),
      });
    };

    const getViewerTelemetry = () => {
      const videoEl = remoteVideoRef.current;
      const visualViewport = typeof window !== "undefined" ? window.visualViewport : null;
      const viewportLeft = 0;
      const viewportTop = 0;
      const viewportWidth = Number(visualViewport?.width) || window.innerWidth || 0;
      const viewportHeight = Number(visualViewport?.height) || window.innerHeight || 0;
      const viewportScale = Number(visualViewport?.scale) || 1;
      const pageVisible = typeof document !== "undefined" ? document.visibilityState === "visible" : true;
      const isActive = pageVisible && (focused || isMobileViewer);
      const sourceWidth = Number(videoEl?.videoWidth) || 0;
      const sourceHeight = Number(videoEl?.videoHeight) || 0;

      let visibleFrame = { x: 0, y: 0, width: 0, height: 0 };
      if (videoEl) {
        const rect = videoEl.getBoundingClientRect();
        const elementWidth = Number(rect.width) || 0;
        const elementHeight = Number(rect.height) || 0;

        let contentLeft = rect.left;
        let contentTop = rect.top;
        let contentWidth = elementWidth;
        let contentHeight = elementHeight;

        // The video uses object-fit: contain, so the visible stream can be letterboxed
        // inside the element box. Measure against the actual content rect, not the full
        // element rect, or the host preview box drifts on mobile.
        if (elementWidth > 0 && elementHeight > 0 && sourceWidth > 0 && sourceHeight > 0) {
          const elementAspect = elementWidth / elementHeight;
          const sourceAspect = sourceWidth / sourceHeight;
          if (sourceAspect > elementAspect) {
            contentHeight = elementWidth / sourceAspect;
            contentTop = rect.top + Math.max(0, (elementHeight - contentHeight) / 2);
          } else if (sourceAspect < elementAspect) {
            contentWidth = elementHeight * sourceAspect;
            contentLeft = rect.left + Math.max(0, (elementWidth - contentWidth) / 2);
          }
        }

        const contentRight = contentLeft + contentWidth;
        const contentBottom = contentTop + contentHeight;
        const intersectionLeft = Math.max(contentLeft, viewportLeft);
        const intersectionTop = Math.max(contentTop, viewportTop);
        const intersectionRight = Math.min(contentRight, viewportLeft + viewportWidth);
        const intersectionBottom = Math.min(contentBottom, viewportTop + viewportHeight);
        const visibleWidth = Math.max(0, intersectionRight - intersectionLeft);
        const visibleHeight = Math.max(0, intersectionBottom - intersectionTop);
        if (contentWidth > 0 && contentHeight > 0 && visibleWidth > 0 && visibleHeight > 0) {
          visibleFrame = {
            x: clampRatio((intersectionLeft - contentLeft) / contentWidth),
            y: clampRatio((intersectionTop - contentTop) / contentHeight),
            width: clampRatio(visibleWidth / contentWidth),
            height: clampRatio(visibleHeight / contentHeight),
          };
        }
      }

      let windowFrame = { x: 0, y: 0, width: 1, height: 1 };
      if (sourceWidth > 0 && sourceHeight > 0 && viewportWidth > 0 && viewportHeight > 0) {
        const scaledSourceWidth = sourceWidth * viewportScale;
        const scaledSourceHeight = sourceHeight * viewportScale;
        const widthRatio = clampRatio(viewportWidth / scaledSourceWidth);
        const heightRatio = clampRatio(viewportHeight / scaledSourceHeight);
        const hasVisibleIntersection = visibleFrame.width > 0 && visibleFrame.height > 0;
        windowFrame = {
          x: hasVisibleIntersection ? visibleFrame.x : 0,
          y: hasVisibleIntersection ? visibleFrame.y : 0,
          width: Math.min(hasVisibleIntersection ? visibleFrame.width : 1, widthRatio || 1),
          height: Math.min(hasVisibleIntersection ? visibleFrame.height : 1, heightRatio || 1),
        };
      }

      return {
        active: isActive,
        pageVisible,
        focused,
        viewportWidth: Math.round(viewportWidth),
        viewportHeight: Math.round(viewportHeight),
        viewportScale: Math.round(viewportScale * 100) / 100,
        sourceWidth,
        sourceHeight,
        visibleFrame,
        windowFrame,
        updatedAt: Date.now(),
      };
    };

    const sendViewerTelemetry = async ({ force = false } = {}) => {
      if (destroyed) return;
      if (telemetryInFlight) {
        queuedTelemetryForce = queuedTelemetryForce || force;
        return;
      }
      telemetryInFlight = true;
      const payload = getViewerTelemetry();
      const signature = JSON.stringify({
        active: payload.active,
        pageVisible: payload.pageVisible,
        focused: payload.focused,
        viewportWidth: payload.viewportWidth,
        viewportHeight: payload.viewportHeight,
        viewportScale: payload.viewportScale,
        visibleFrame: payload.visibleFrame,
        windowFrame: payload.windowFrame,
      });
      if (!force && signature === lastTelemetrySignature) {
        telemetryInFlight = false;
        if (queuedTelemetryForce) {
          const nextForce = queuedTelemetryForce;
          queuedTelemetryForce = false;
          void sendViewerTelemetry({ force: nextForce });
        }
        return;
      }
      lastTelemetrySignature = signature;
      try {
        await sendSignal("viewer_state", payload);
      } catch (_err) {
      } finally {
        telemetryInFlight = false;
        if (queuedTelemetryForce) {
          const nextForce = queuedTelemetryForce;
          queuedTelemetryForce = false;
          void sendViewerTelemetry({ force: nextForce });
        }
      }
    };

    const scheduleNextTelemetry = () => {
      if (destroyed) return;
      if (telemetryTimer) {
        clearTimeout(telemetryTimer);
      }
      const nextDelay =
        Date.now() < recentTelemetryActivityUntil
          ? TRUST_VIEWER_TELEMETRY_FAST_POLL_MS
          : TRUST_VIEWER_TELEMETRY_IDLE_POLL_MS;
      telemetryTimer = window.setTimeout(() => {
        void sendViewerTelemetry();
        scheduleNextTelemetry();
      }, nextDelay);
    };

    const markTelemetryActivity = ({ force = false } = {}) => {
      recentTelemetryActivityUntil = Date.now() + TRUST_VIEWER_TELEMETRY_ACTIVE_WINDOW_MS;
      void sendViewerTelemetry({ force });
      scheduleNextTelemetry();
    };

    const requestHostReconnect = async (reason) => {
      if (destroyed) return;
      const now = Date.now();
      if (now - lastReconnectRequestAt < TRUST_VIEWER_RECONNECT_REQUEST_THROTTLE_MS) return;
      lastReconnectRequestAt = now;
      try {
        await sendSignal("viewer_state", {
          ...getViewerTelemetry(),
          reconnectRequested: true,
          reconnectReason: reason,
        });
      } catch (_err) {
        // The regular signal poll will keep retrying.
      }
    };

    const hasLiveRemoteTrack = () => {
      const videoStream = remoteVideoRef.current?.srcObject;
      return (
        !!videoStream &&
        typeof videoStream.getTracks === "function" &&
        videoStream.getTracks().some((track) => track.readyState === "live")
      );
    };

    const startRealtime = async () => {
      if (realtimeStarting || peerRef.current) return;
      realtimeStarting = true;
      setRemoteVideoReady(false);
      const iceServers = await resolveViewerIceServers({ token, viewerKey });
      if (cancelled) {
        realtimeStarting = false;
        return;
      }

      const pc = new RTCPeerConnection({ iceServers });
      peerRef.current = pc;
      realtimeStarting = false;

      const flushPendingRemoteCandidates = async () => {
        if (!pc.remoteDescription || pendingRemoteCandidatesRef.current.length === 0) return;
        const queued = [...pendingRemoteCandidatesRef.current];
        pendingRemoteCandidatesRef.current = [];
        for (const candidate of queued) {
          try {
            await pc.addIceCandidate(new RTCIceCandidate(candidate));
          } catch (_err) {
            // ignore malformed candidate
          }
        }
      };

      pc.ontrack = (event) => {
        const [stream] = event.streams || [];
        if (!stream || !remoteVideoRef.current) return;
        setRemoteVideoReady(false);
        remoteVideoRef.current.srcObject = stream;
        remoteVideoRef.current
          .play()
          .then(() => {
            setRemoteVideoReady(true);
            setResumePromptVisible(false);
            setLiveStatus("Live now. This Trust Session is view-only.");
          })
          .catch(() => {
            setResumePromptVisible(true);
            setLiveStatus("Live stream available. Press play if your browser blocks autoplay.");
          });
      };

      pc.onicecandidate = async (event) => {
        if (!event.candidate) return;
        try {
          await sendSignal("candidate", { candidate: event.candidate });
        } catch (_err) {
          // ignore transient signaling errors
        }
      };

      pc.onconnectionstatechange = () => {
        const state = pc.connectionState;
        setConnectionState(state);
        if (state === "connected") {
          setLiveStatus("Connected. Verifying stream...");
          markTelemetryActivity({ force: true });
        } else if (state === "failed" || state === "disconnected") {
          setLiveStatus("Connection interrupted. Reconnecting...");
        }
      };

      const pollSignals = async () => {
        try {
          const res = await fetch(
            `${API_BASE}/api/trust-session/${encodeURIComponent(token)}/signals?role=viewer&cursor=${cursorRef.current}&viewerKey=${encodeURIComponent(viewerKey)}`,
            { cache: "no-store" }
          );
          const payload = await res.json().catch(() => ({}));
          if (!res.ok) {
            if (payload?.error) setError(payload.error);
            return;
          }
          const signals = Array.isArray(payload?.signals) ? payload.signals : [];
          if (Number.isFinite(payload?.nextCursor)) cursorRef.current = payload.nextCursor;

          for (const signal of signals) {
            const signalType = String(signal?.signal_type || "").toLowerCase();
            if (signalType === "offer" && signal?.payload?.sdp) {
              const nextOfferId = String(signal.payload.offerId || makeLegacyOfferId(signal));
              if (activeOfferIdRef.current === nextOfferId && pc.remoteDescription) {
                continue;
              }
              activeConnectionEpochRef.current = String(signal.payload.connectionEpoch || activeConnectionEpochRef.current || "");
              activeOfferIdRef.current = nextOfferId;
              pendingRemoteCandidatesRef.current = [];
              await pc.setRemoteDescription(new RTCSessionDescription(signal.payload.sdp));
              await flushPendingRemoteCandidates();
              const answer = await pc.createAnswer();
              await pc.setLocalDescription(answer);
              await sendSignal("answer", {
                sdp: pc.localDescription,
                connectionEpoch: activeConnectionEpochRef.current,
                offerId: nextOfferId,
              });
              setLiveStatus("Connecting to the live stream...");
            } else if (signalType === "candidate" && signal?.payload?.candidate) {
              const signalOfferId = String(signal.payload.offerId || "");
              if (signalOfferId && activeOfferIdRef.current && signalOfferId !== activeOfferIdRef.current) {
                continue;
              }
              const candidate = signal.payload.candidate;
              if (pc.remoteDescription) {
                try {
                  await pc.addIceCandidate(new RTCIceCandidate(candidate));
                } catch (_err) {
                  // ignore bad candidates
                }
              } else {
                pendingRemoteCandidatesRef.current.push(candidate);
              }
            } else if (signalType === "end") {
              setLiveStatus("This Trust Session has ended.");
              stopRealtime();
            }
          }
        } catch (_err) {
          // silent retry
        }
      };

      pollSignals();
      pollTimerRef.current = setInterval(pollSignals, TRUST_VIEWER_SIGNAL_POLL_MS);
    };

    const restartRealtimeIfNeeded = async (reason) => {
      const pc = peerRef.current;
      const state = pc?.connectionState || "closed";

      void requestHostReconnect(reason);
      if (state === "failed" || state === "closed" || state === "disconnected" || !hasLiveRemoteTrack()) {
        setConnectionState("reconnecting");
        setLiveStatus("Reconnecting to the live stream...");
        stopRealtime();
        await startRealtime();
      }
    };

    const handleFocus = () => {
      focused = true;
      markTelemetryActivity({ force: true });
    };

    const handleBlur = () => {
      focused = false;
      markTelemetryActivity({ force: true });
      sendPointerInactive();
    };

    const handleViewportChange = () => {
      markTelemetryActivity();
    };

    const handleVisibilityChange = () => {
      const isVisible = typeof document !== "undefined" && document.visibilityState === "visible";
      focused =
        isVisible
          ? true
          : typeof document !== "undefined"
            ? document.hasFocus()
            : focused;
      markTelemetryActivity({ force: true });
      if (isVisible) {
        void restartRealtimeIfNeeded("visibility");
      } else {
        sendPointerInactive();
      }
    };

    const handlePageShow = () => {
      focused = true;
      markTelemetryActivity({ force: true });
      void restartRealtimeIfNeeded("pageshow");
    };

    const handlePointerMove = (event) => {
      if (event.pointerType && event.pointerType !== "mouse") return;
      const now = Date.now();
      if (now - lastPointerMoveAt < TRUST_VIEWER_POINTER_MOVE_THROTTLE_MS) return;
      lastPointerMoveAt = now;
      const payload = getPointerPayload(event, "move");
      if (payload) void sendViewerPointer(payload);
    };

    const handlePointerDown = (event) => {
      const pointerType = event.pointerType || "mouse";
      const payload = getPointerPayload(event, pointerType === "mouse" ? "click" : "tap");
      if (payload) void sendViewerPointer(payload);
    };

    const handlePointerLeave = () => {
      sendPointerInactive();
    };

    const startViewerConnection = async () => {
      await startRealtime();
      await requestHostReconnect("viewer-start");
    };

    void startViewerConnection();
    startupReconnectTimer = window.setTimeout(() => {
      if (!destroyed && !hasLiveRemoteTrack()) {
        void restartRealtimeIfNeeded("startup-timeout");
      }
    }, TRUST_VIEWER_STARTUP_RECONNECT_MS);
    markTelemetryActivity({ force: true });
    const pointerTarget = videoWrapRef.current;
    if (pointerTarget) {
      pointerTarget.addEventListener("pointermove", handlePointerMove, { passive: true });
      pointerTarget.addEventListener("pointerdown", handlePointerDown, { passive: true });
      pointerTarget.addEventListener("pointerleave", handlePointerLeave);
      pointerTarget.addEventListener("pointercancel", handlePointerLeave);
    }
    window.addEventListener("focus", handleFocus);
    window.addEventListener("blur", handleBlur);
    window.addEventListener("pageshow", handlePageShow);
    window.addEventListener("resize", handleViewportChange);
    window.addEventListener("scroll", handleViewportChange, { passive: true });
    document.addEventListener("visibilitychange", handleVisibilityChange);
    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", handleViewportChange);
      window.visualViewport.addEventListener("scroll", handleViewportChange);
    }

    return () => {
      destroyed = true;
      cancelled = true;
      if (telemetryTimer) {
        clearTimeout(telemetryTimer);
      }
      if (startupReconnectTimer) {
        clearTimeout(startupReconnectTimer);
      }
      if (pointerTarget) {
        pointerTarget.removeEventListener("pointermove", handlePointerMove);
        pointerTarget.removeEventListener("pointerdown", handlePointerDown);
        pointerTarget.removeEventListener("pointerleave", handlePointerLeave);
        pointerTarget.removeEventListener("pointercancel", handlePointerLeave);
      }
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("blur", handleBlur);
      window.removeEventListener("pageshow", handlePageShow);
      window.removeEventListener("resize", handleViewportChange);
      window.removeEventListener("scroll", handleViewportChange);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener("resize", handleViewportChange);
        window.visualViewport.removeEventListener("scroll", handleViewportChange);
      }
      stopRealtime();
    };
  }, [sessionBundle, token, viewerKey]);

  if (loading) {
    return (
      <div className="page trust-view-page">
        <div className="panel trust-brand-shell">
          <h2>Loading Trust Session...</h2>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page trust-view-page">
        <div className="panel trust-brand-shell">
          <h2>Trust Session unavailable</h2>
          <p className="muted" style={{ marginTop: 8 }}>
            {error}
          </p>
        </div>
      </div>
    );
  }

  const session = sessionBundle?.session || {};
  const owner = sessionBundle?.owner || {};
  const panelTimestamp = owner?.verifiedAt ? new Date(owner.verifiedAt).toLocaleString() : new Date().toLocaleString();
  const ownerNameParts = String(owner?.name || "").trim().split(/\s+/).filter(Boolean);
  const ownerFirstName = ownerNameParts[0] || "";
  const ownerLastName = ownerNameParts.slice(1).join(" ");
  const ownerInitials = `${ownerFirstName?.[0] || ""}${ownerLastName?.[0] || ""}`.trim() || "CB";
  const profileUrl =
    owner?.profileUrl ||
    (owner?.publicSlug
      ? `${typeof window !== "undefined" ? window.location.origin : ""}/p/${encodeURIComponent(owner.publicSlug)}`
      : "");
  const cardLicenses = Array.isArray(owner?.licenses) ? owner.licenses : [];
  const cardProfile = {
    firstName: ownerFirstName,
    lastName: ownerLastName,
    email: owner?.email || "",
    phone: owner?.phone || "",
    company: owner?.company || "",
    profilePhoto: owner?.profilePhoto || "",
    publicLink: owner?.profileUrl || "",
    title: owner?.title || "",
    links: [],
    visibility: {
      email: !!owner?.email,
      phone: !!owner?.phone,
      company: !!owner?.company,
      title: !!owner?.title,
      businessAddress: false,
      twitter: false,
      facebook: false,
      instagram: false,
      linkedin: false,
    },
  };

  const toggleFullscreen = async () => {
    if (!canToggleFullscreen || !videoWrapRef.current || typeof document === "undefined") return;

    try {
      if (fullscreenSupported) {
        if (isFullscreen) {
          if (document.exitFullscreen) {
            await document.exitFullscreen();
          } else if (document.webkitExitFullscreen) {
            document.webkitExitFullscreen();
          } else if (document.msExitFullscreen) {
            document.msExitFullscreen();
          }
          return;
        }

        setIsInlineFullscreen(false);
        const target = videoWrapRef.current;
        if (target.requestFullscreen) {
          await target.requestFullscreen();
        } else if (target.webkitRequestFullscreen) {
          target.webkitRequestFullscreen();
        } else if (target.msRequestFullscreen) {
          target.msRequestFullscreen();
        }
        return;
      }

      setIsInlineFullscreen((current) => !current);
    } catch (_err) {
      // ignore fullscreen API failures
    }
  };

  const resumeLiveVideo = async () => {
    try {
      await remoteVideoRef.current?.play?.();
      setResumePromptVisible(false);
      setLiveStatus("Live now. This Trust Session is view-only.");
    } catch (_err) {
      setResumePromptVisible(true);
      setLiveStatus("Live stream available. Tap resume to continue.");
    }
  };

  const viewerFullscreenLabel = isViewerFullscreen ? "Exit full screen preview" : "Enter full screen preview";
  const showLoadingPreview =
    !remoteVideoReady &&
    !resumePromptVisible &&
    liveStatus !== "This Trust Session has ended.";

  return (
    <div className="page trust-view-page">
      <div className="panel trust-view-panel trust-brand-shell">
        <div className="trust-brand-topbar">
          <img src="/clearbadge-logo-color.png" alt="ClearBadge" className="trust-brand-logo" />
          <span className="badge success">Trust Session</span>
        </div>
        <div className="trust-view-header">
          <div>
            <h1>{owner?.name || "Verified Professional"}</h1>
            <p className="muted" style={{ marginTop: 8 }}>
              You are viewing a live Trust Session powered by ClearBadge&trade;
            </p>
          </div>
        </div>

        <div className="trust-view-grid">
          <div
            ref={videoWrapRef}
            className={`trust-video-wrap${canToggleFullscreen ? " is-fullscreen-capable" : ""}${isViewerFullscreen ? " is-fullscreen" : ""}${isInlineFullscreen ? " is-inline-fullscreen" : ""}`}
            onClick={canToggleFullscreen ? () => { void toggleFullscreen(); } : undefined}
            onKeyDown={
              canToggleFullscreen
                ? (event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    void toggleFullscreen();
                  }
                }
                : undefined
            }
            role={canToggleFullscreen ? "button" : undefined}
            tabIndex={canToggleFullscreen ? 0 : undefined}
            aria-label={canToggleFullscreen ? viewerFullscreenLabel : undefined}
          >
            <video
              ref={remoteVideoRef}
              autoPlay
              playsInline
              muted
              className="trust-video"
              onLoadedData={() => setRemoteVideoReady(true)}
              onPlaying={() => setRemoteVideoReady(true)}
              onWaiting={() => setRemoteVideoReady(false)}
            />
            {showLoadingPreview && (
              <div className="trust-video-loading-overlay" aria-live="polite">
                Loading Preview...
              </div>
            )}
            {resumePromptVisible && (
              <button
                type="button"
                className="primary trust-video-resume-btn"
                onClick={(event) => {
                  event.stopPropagation();
                  void resumeLiveVideo();
                }}
              >
                Tap to resume
              </button>
            )}
            {canToggleFullscreen && (
              <button
                type="button"
                className="trust-video-fullscreen-btn"
                onClick={(event) => {
                  event.stopPropagation();
                  void toggleFullscreen();
                }}
                aria-label={viewerFullscreenLabel}
              >
                {isViewerFullscreen ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="m14 10 7-7" />
                    <path d="M20 10h-6V4" />
                    <path d="m3 21 7-7" />
                    <path d="M4 14h6v6" />
                  </svg>
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M3 7V5a2 2 0 0 1 2-2h2" />
                    <path d="M17 3h2a2 2 0 0 1 2 2v2" />
                    <path d="M21 17v2a2 2 0 0 1-2 2h-2" />
                    <path d="M7 21H5a2 2 0 0 1-2-2v-2" />
                    <rect width="10" height="8" x="7" y="8" rx="1" />
                  </svg>
                )}
              </button>
            )}
            <div className="trust-video-caption">{liveStatus}</div>
            <div className="trust-conn-state">Connection: {connectionState}</div>
          </div>
          <aside className="trust-verified-panel">
            <h3>Trust Session Info</h3>
            <p>
              <strong>Name:</strong> {owner?.name || "Unavailable"}
            </p>
            <p>
              <strong>License type:</strong> {owner?.licenseType || "Unavailable"}
            </p>
            <p>
              <strong>Profile link:</strong>{" "}
              {profileUrl ? (
                <a href={profileUrl} target="_blank" rel="noopener noreferrer" className="trust-profile-link">
                  {profileUrl}
                </a>
              ) : (
                "Unavailable"
              )}
            </p>
            <p>
              <strong>Timestamp:</strong> {panelTimestamp}
            </p>
            {session?.session_code && (
              <div className="trust-session-code-box">
                <div className="muted small">Session code</div>
                <div className="trust-session-code">{session.session_code}</div>
                <p className="muted small">Ask the licensed professional to repeat this code aloud.</p>
              </div>
            )}
            <button
              type="button"
              className="ghost small trust-card-toggle-btn"
              onClick={() => setShowDigitalCard(true)}
            >
              Show Digital Business Card
            </button>
          </aside>
        </div>
      </div>
      {showDigitalCard && (
        <div className="trust-card-modal-backdrop" onClick={() => setShowDigitalCard(false)}>
          <div
            className="trust-card-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="trust-card-modal-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="trust-card-modal-header">
              <h3 id="trust-card-modal-title">Digital Business Card</h3>
              <button
                type="button"
                className="trust-card-modal-close"
                onClick={() => setShowDigitalCard(false)}
                aria-label="Close digital business card"
              >
                <svg
                  className="modal-close-icon"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M18 6 6 18" />
                  <path d="m6 6 12 12" />
                </svg>
              </button>
            </div>
            <div className="trust-card-modal-body">
              <DigitalCard
                profile={cardProfile}
                initials={ownerInitials}
                licenses={cardLicenses}
                stats={{}}
                cardColor={owner?.cardColor || DEFAULT_CARD_COLOR}
                cardBackgroundUrl={owner?.cardBackgroundUrl || ""}
                publicSlug={owner?.publicSlug || ""}
                profileId={owner?.id || ""}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
